import { useEffect, useState } from 'react'
import { localDateString } from '../utils/dates'

interface Props {
  date: string
  onChange: (date: string) => void
  datesWithEntries: Set<string>
}

export function MiniCalendar({ date, onChange, datesWithEntries }: Props) {
  const today = localDateString()

  const [viewYear, setViewYear] = useState(() => parseInt(date.slice(0, 4)))
  const [viewMonth, setViewMonth] = useState(() => parseInt(date.slice(5, 7)) - 1)

  useEffect(() => {
    setViewYear(parseInt(date.slice(0, 4)))
    setViewMonth(parseInt(date.slice(5, 7)) - 1)
  }, [date])

  const todayYear = parseInt(today.slice(0, 4))
  const todayMonth = parseInt(today.slice(5, 7)) - 1
  const isLatestMonth = viewYear > todayYear || (viewYear === todayYear && viewMonth >= todayMonth)

  const earliestDate = datesWithEntries.size > 0 ? [...datesWithEntries].sort()[0] : null
  const earliestYear = earliestDate ? parseInt(earliestDate.slice(0, 4)) : null
  const earliestMonth = earliestDate ? parseInt(earliestDate.slice(5, 7)) - 1 : null
  const isEarliestMonth = earliestYear !== null && earliestMonth !== null
    && (viewYear < earliestYear || (viewYear === earliestYear && viewMonth <= earliestMonth))

  function prevMonth() {
    if (isEarliestMonth) return
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1) }
    else setViewMonth(m => m - 1)
  }

  function nextMonth() {
    if (isLatestMonth) return
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1) }
    else setViewMonth(m => m + 1)
  }

  const mm = String(viewMonth + 1).padStart(2, '0')
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate()
  const startDow = new Date(viewYear, viewMonth, 1).getDay()
  const monthLabel = new Date(viewYear, viewMonth, 1)
    .toLocaleString('default', { month: 'long', year: 'numeric' })

  return (
    <div style={{ userSelect: 'none', maxWidth: '280px', margin: '1rem 0' }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
        <button type="button" onClick={prevMonth} disabled={isEarliestMonth} style={{ opacity: isEarliestMonth ? 0.3 : 1 }}>←</button>
        <span style={{ flex: 1, textAlign: 'center', fontSize: '0.9em' }}>
          {monthLabel.toLowerCase()}
        </span>
        <button type="button" onClick={nextMonth} disabled={isLatestMonth} style={{ opacity: isLatestMonth ? 0.3 : 1 }}>→</button>
      </div>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(7, 1fr)',
        gap: '1px',
        textAlign: 'center',
        fontSize: '0.85em',
      }}>
        {['su', 'mo', 'tu', 'we', 'th', 'fr', 'sa'].map(d => (
          <div key={d} style={{ opacity: 0.5, padding: '0.2rem 0' }}>{d}</div>
        ))}
        {Array.from({ length: startDow }, (_, i) => <div key={`pad-${i}`} />)}
        {Array.from({ length: daysInMonth }, (_, i) => {
          const day = i + 1
          const dd = String(day).padStart(2, '0')
          const dateStr = `${viewYear}-${mm}-${dd}`
          const isSelected = dateStr === date
          const isSelectable = datesWithEntries.has(dateStr)

          return (
            <div
              key={day}
              onClick={() => { if (isSelectable) onChange(dateStr) }}
              style={{
                cursor: isSelectable ? 'pointer' : 'default',
                opacity: isSelectable ? 1 : 0.2,
                padding: '0.15rem',
                borderRadius: '3px',
                background: isSelected ? 'var(--color-fg)' : 'transparent',
                color: isSelected ? 'var(--color-bg)' : 'inherit',
              }}
            >
              {day}
            </div>
          )
        })}
      </div>
    </div>
  )
}
